import { useRef, useState, useEffect } from "react";

import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Cancel";
import { Icon } from "@iconify/react";
import Switch from "@mui/material/Switch";
import { red, green } from "@mui/material/colors";
import { alpha, styled } from "@mui/material/styles";
import * as lodash from "lodash";
import Tooltip from "@mui/material/Tooltip";
import store from "store2";

import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  storyFilterBoxState,
  projectPickerState,
  activeLabelsState,
  negateLabelsState,
  reloadState,
} from "./RecoilStates";

import { http_post, activeProject } from "./Auth";
import * as u from "./utility";

import { AutocompleteButton } from "./AutocompleteButton";
import log from "./logger";

const ColoredSwitch = styled(Switch, {
  shouldForwardProp: (prop) => prop !== "color" && prop !== "colorChecked",
})(({ theme, color, colorChecked }) => {
  const checked = colorChecked
    ? {
        "& .MuiSwitch-switchBase.Mui-checked": {
          color: colorChecked,
          "&:hover": {
            backgroundColor: alpha(
              colorChecked,
              theme.palette.action.hoverOpacity
            ),
          },
        },
        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
          backgroundColor: colorChecked,
        },
      }
    : {};

  const unchecked = color
    ? {
        "& .MuiSwitch-switchBase": {
          color: color,
          "&:hover": {
            backgroundColor: alpha(color, theme.palette.action.hoverOpacity),
          },
        },
        // "& .MuiSwitch-switchBase + .MuiSwitch-track": {
        //   backgroundColor: color,
        // },
      }
    : {};

  return { ...checked, ...unchecked };
});

export function LabelsData() {
  const reloadData = useRecoilValue(reloadState);
  const setActiveLabels = useSetRecoilState(activeLabelsState);
  const project = useRecoilValue(projectPickerState);

  const getData = async (project) => {
    try {
      const response = await http_post("stories", {
        activelabels: true,
        project: project,
      });
      let newLabels = response.data.activelabels;

      let mapping = {};
      mapping[":white_check_mark:"] = {
        name: ":white_check_mark:",
        display: { icon: "material-symbols:check-box-rounded" },
      };
      mapping["github"] = { name: "github", display: { icon: "uil:github" } };
      mapping["slack"] = { name: "slack", display: { icon: "uil:slack" } };
      mapping["gitlab"] = { name: "gitlab", display: { icon: "uil:gitlab" } };

      const channels = newLabels.channels;
      const objects = newLabels.objects;

      for (let i = 0; i < newLabels.labels.length; ++i) {
        const l = newLabels.labels[i];
        let displayName = l;
        if (channels && l in channels) {
          displayName = `#${channels[l].name_normalized}`;
        } else if (objects && l in objects) {
          displayName = objects[l].full_name;
        }
        const o = l in mapping ? mapping[l] : { name: l, display: displayName };
        o.index = i;
        mapping[l] = o;
      }

      setActiveLabels((prev) => {
        for (let [k] of Object.entries(mapping)) {
          if (!prev || !(k in prev)) {
            return mapping;
          }
        }
        return prev;
      });
    } catch (err) {
      log.debug(err);
    } finally {
    }
  };

  useEffect(() => {
    const proj = project || activeProject;
    if (proj) {
      getData(proj);
    }
  }, [reloadData, project]);
}

export function StoryLabelFilterBox({ newLabel }) {
  const [labels, setLabels] = useState([]);
  const [selected, setSelected] = useRecoilState(storyFilterBoxState);
  const project = useRecoilValue(projectPickerState);
  const [negate, setNegate] = useRecoilState(negateLabelsState);
  const ref = useRef({});
  const activeLabels = useRecoilValue(activeLabelsState);

  const updateSelection = (selection) => {
    setSelected((prev) => {
      const next = { ...prev, [activeProject]: selection };
      const key = u.generateCacheKey("StoryLabelFilterBox", activeProject);
      store(key, JSON.stringify(next));

      return next;
    });
  };

  const getSelected = () => {
    return selected && activeProject in selected ? selected[activeProject] : [];
  };

  useEffect(() => {
    if (project) {
      const key = u.generateCacheKey("StoryLabelFilterBox", project);
      const fromCache = JSON.parse(store.get(key, "{}"));
      setSelected((prev) => {
        if (!prev || !lodash.isEqual(fromCache, prev)) {
          return fromCache;
        }
        return prev;
      });
    }
  }, [project]);

  useEffect(() => {
    setLabels((prev) => {
      if (newLabel && newLabel.length) {
        let result = [...prev];
        const names = prev.map((x) => x.name);
        for (let l of newLabel) {
          if (!names.includes(l)) {
            result.push({ name: l, display: l });
          }
        }
        return result;
      }
      return prev;
    });
  }, [newLabel, setLabels]);

  useEffect(() => {
    if (!activeLabels || !selected) {
      return;
    }

    let mapping = { ...activeLabels };
    let listing = [];

    for (let [k, v] of Object.entries(mapping)) {
      if (v.index >= 0) {
        listing.push(v);
      }
    }
    listing.sort((a, b) => a.index - b.index);

    // add any new labels that were created in the ui previously and are missing from backend
    for (let l of labels) {
      if (!mapping[l.name]) {
        listing.push(l);
        mapping[l.name] = l;
      }
    }

    let newSelected = [];
    let prevSelected = getSelected();
    for (let s of prevSelected) {
      if (s in mapping) {
        newSelected.push(s);
      }
    }

    if (!u.equalArrays(newSelected, prevSelected)) {
      updateSelection(newSelected);
    }

    setLabels(listing);
  }, [activeLabels, selected]);

  const replaceSelectedFilter = (selected, label) => {
    if (selected.length === 1 && selected[0] === label) {
      updateSelection([]);
    } else {
      updateSelection([label]);
    }
  };

  const addLabelToSelection = (selected, label) => {
    const found = selected.indexOf(label);
    if (found === -1) {
      // not found
      const newFilters = [label, ...selected];
      updateSelection(newFilters);
    } else {
      let newFilters = [...selected];
      newFilters.splice(found, 1);
      updateSelection(newFilters);
    }
  };

  const handleClick = (e, label) => {
    const selected = getSelected();
    if (e.metaKey || e.shiftKey || e.ctrlKey) {
      addLabelToSelection(selected, label);
    } else {
      replaceSelectedFilter(selected, label);
    }

    if (ref.current.prevClick) {
      const elapsed = e.timeStamp - ref.current.prevClick;
      if (e.detail > 1 && elapsed < 200) {
        const url = u.getStoryUrlFromId(label, activeProject);
        window.open(url, "_blank", "noreferrer");
      }
    }
    ref.current.prevClick = e.timeStamp;
  };

  const handleClearSelection = () => {
    updateSelection([]);
  };

  const handleLabelSearch = (value, e) => {
    const found = labels.find((e) => e.display === value || e.name === value);
    if (found) {
      handleClick(e, found.name);
    }
  };

  const handleNegate = () => {
    u.writeLocalStorage("NegateLabels", negate ? "" : "true");
    setNegate((prev) => !prev);
  };

  const getBackgroundColor = (label) => {
    return getSelected().includes(label)
      ? negate
        ? "tomato"
        : "green"
      : "#eeeeee";
  };

  const getColor = (label) => {
    return getSelected().includes(label) ? "white" : "black";
  };

  const getIconColor = (label) => {
    return getSelected().includes(label)
      ? negate
        ? "tomato"
        : "green"
      : "grey";
  };

  const anyFiltersSelected = () => {
    return getSelected().length > 0;
  };

  return (
    <Grid container spacing={0} sx={{ m: 0.5 }}>
      <Grid item>
        <ColoredSwitch
          colorChecked={red[600]}
          color={anyFiltersSelected() ? green[400] : undefined}
          onChange={handleNegate}
          checked={negate}
          inputProps={{ "aria-label": "Negate switch" }}
          size="small"
        />
      </Grid>
      <Grid item>
        <Tooltip
          title="Clear selection"
          placement="top-end"
          arrow
          enterDelay={500}
        >
          <span>
            <IconButton
              size="small"
              disabled={!anyFiltersSelected()}
              onClick={handleClearSelection}
              style={{ color: anyFiltersSelected() ? "lightpink" : undefined }}
            >
              <CancelIcon fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>
      </Grid>
      <Grid item>
        <AutocompleteButton
          variant="standard"
          options={labels.map((e) =>
            typeof e.display === "string" ? e.display : e.name
          )}
          onSelected={handleLabelSearch}
          sx={{ minWidth: "100" }}
        >
          <SearchIcon fontSize="inherit" />
        </AutocompleteButton>
      </Grid>
      {labels.map((item, index) => (
        <Grid item key={index}>
          {typeof item.display === "string" && (
            <Chip
              size="small"
              style={{
                background: getBackgroundColor(item.name),
                color: getColor(item.name),
              }}
              label={item.display}
              onClick={(e) => {
                handleClick(e, item.name);
              }}
            />
          )}
          {typeof item.display === "object" && (
            <IconButton
              style={{ color: getIconColor(item.name) }}
              size="small"
              onClick={(e) => {
                handleClick(e, item.name);
              }}
            >
              <Icon icon={item.display.icon} />
            </IconButton>
          )}
        </Grid>
      ))}
    </Grid>
  );
}
